import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Col, Row } from 'react-bootstrap';
import Button from '@mui/material/Button';
import FullscreenSharpIcon from '@mui/icons-material/FullscreenSharp';
import ApplicationLayout from 'v3/layouts/Application';
import { cooperplaceApi } from 'services/api';
import { usePermission } from 'hooks';
import { StyledImg, TreeViewStyle, LoadingSpinner } from './style';
import PowerBiRlsAuth from './PowerBiRlsAuth';

function groupBy(data) {
  const grouped = {};
  data.forEach((item) => {
    if (!grouped[item.workspace_name]) {
      grouped[item.workspace_name] = { [item.departament_name]: [item] };
    } else if (!grouped[item.workspace_name][item.departament_name]) {
      grouped[item.workspace_name][item.departament_name] = [item];
    } else {
      grouped[item.workspace_name][item.departament_name].push(item);
    }
  });
  return grouped;
}

const makeId = (workspace, department, panelName) =>
  [workspace, department, panelName]
    .filter(Boolean)
    .map((s) => String(s).replace(/[|]/g, '¦'))
    .join('|');

export default function LoadPanelOfBi() {
  usePermission('VISUALIZA_DASHBOARD_TORRE_DE_CONTROLE_BI', { redirect: true });

  const classes = TreeViewStyle();
  const iframeRef = useRef(null);
  const reportHostRef = useRef(null);

  const [groupedData, setGroupedData] = useState({});
  const [panelsRaw, setPanelsRaw] = useState([]);
  const [openWorkspaces, setOpenWorkspaces] = useState({});
  const [openDepartments, setOpenDepartments] = useState({});
  const [biUrl, setBiUrl] = useState(null);
  const [biRls, setRls] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPanels, setLoadingPanels] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const usuario = JSON.parse(localStorage.getItem('usuario'));
        const { data } = await cooperplaceApi.get(`carg-panel-users/byuser/${usuario.id}`);
        const safeData = Array.isArray(data) ? data : [];
        
        setPanelsRaw(safeData);
        setGroupedData(groupBy(safeData));
      } catch (err) {
        console.error('Erro ao carregar painéis BI:', err);
        setGroupedData({});
        setPanelsRaw([]);
      } finally {
        setLoadingPanels(false);
      }
    })();
  }, []);

  const toggleWorkspace = useCallback((workspace) => {
    setOpenWorkspaces((prev) => ({ ...prev, [workspace]: !prev[workspace] }));
  }, []);

  const toggleDepartment = useCallback((workspace, department) => {
    const key = `${workspace}__${department}`;
    setOpenDepartments((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const panelById = useMemo(() => {
    const map = new Map();
    Object.entries(groupedData).forEach(([workspace, departments]) => {
      Object.entries(departments).forEach(([department, panels]) => {
        panels.forEach((panel) => {
          const id = makeId(workspace, department, panel.name ?? panel.id ?? panel.url);
          map.set(id, panel);
        });
      });
    });
    return map;
  }, [groupedData]);

  const handlePanelClick = useCallback((panel) => {
    setBiUrl(panel.url);
    setRls(panel.rls);
    setLoading(true);
    setIframeKey((k) => k + 1);
  }, []);

  const renderLeafLabel = useCallback(
    (panel) => (
      <span
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handlePanelClick(panel);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handlePanelClick(panel);
          }
        }}
      >
        {panel.name}
      </span>
    ),
    [handlePanelClick]
  );

  useEffect(() => {
    const onFsChange = () => {
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setFullScreen(Boolean(fsEl));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    document.addEventListener('MSFullscreenChange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
      document.removeEventListener('MSFullscreenChange', onFsChange);
    };
  }, []);

  const toggleIframeFullScreen = useCallback(() => {
    const el =
      reportHostRef.current ||
      iframeRef.current ||
      document.getElementById('reportContainer');

    if (!el) return;

    const enter =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen;

    const exit =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;

    if (!fullScreen && enter) {
      enter.call(el);
    } else if (fullScreen && exit) {
      exit.call(document);
    }
  }, [fullScreen]);

  const currentPanel = useMemo(() => {
    if (!biUrl) return null;
    for (const panelsOfDept of Object.values(groupedData || {})) {
      for (const panels of Object.values(panelsOfDept)) {
        const p = panels.find((it) => it.url === biUrl);
        if (p) return p;
      }
    }
    return null;
  }, [groupedData, biUrl]);

  return (
    <>
      <ApplicationLayout
        title="Cooperplace Analytics"
        isOld
        onlyCenterComponent={false}
        fontSize="20px"
      />

      <Row style={{ paddingLeft: '50px', margin: 0 }} md={12}>
        <Col md={2}>
          <div
            style={{
              position: 'relative',
              background: '#f2f2f2',
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              minHeight: 'calc(100vh - 180px)',
              maxHeight: 'calc(100vh - 180px)',
              overflowY: 'auto',
              padding: '12px 10px',
              marginTop: 20,
              marginLeft: 12,
            }}
          >
            {(loading || loadingPanels) && (
              <div
                className={classes.overlay}
                style={{
                  maxWidth: '100%',
                  height: '100%',
                }}
              >
                <LoadingSpinner />
              </div>
            )}

            {!loadingPanels && panelsRaw.length > 0 ? (
              <div style={{ padding: '8px 12px' }}>
                {Object.entries(groupBy(panelsRaw)).map(([workspace, departments]) => (
                  <div
                    key={workspace}
                    style={{
                      marginBottom: 12,
                      paddingBottom: 10,
                      borderBottom: '1px solid #d9d9d9',
                    }}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleWorkspace(workspace)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') toggleWorkspace(workspace);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontWeight: 600,
                        color: '#1f2a37',
                        fontSize: 13,
                        marginBottom: 6,
                        cursor: 'pointer',
                        padding: '4px 2px',
                        borderRadius: 4,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e6e8eb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span style={{ fontSize: 14 }}>
                        {openWorkspaces[workspace] ? '▾' : '▸'}
                      </span>
                      {workspace}
                    </div>
                    {openWorkspaces[workspace] &&
                      Object.entries(departments).map(([department, panels]) => {
                        const deptKey = `${workspace}__${department}`;
                        return (
                          <div key={department} style={{ marginBottom: 8, paddingLeft: 14 }}>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => toggleDepartment(workspace, department)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  toggleDepartment(workspace, department);
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                color: '#1f2a37',
                                fontSize: 12.5,
                                marginBottom: 4,
                                cursor: 'pointer',
                                padding: '2px 2px',
                                borderRadius: 4,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#e6e8eb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <span style={{ fontSize: 13 }}>
                                {openDepartments[deptKey] ? '▾' : '▸'}
                              </span>
                              {department}
                            </div>
                            {openDepartments[deptKey] &&
                              panels.map((panel) => (
                                <div
                                  key={panel.url}
                                  onClick={() => handlePanelClick(panel)}
                                  style={{
                                    cursor: 'pointer',
                                    padding: '4px 6px',
                                    borderRadius: 4,
                                    color: '#1f2a37',
                                    fontSize: 12.5,
                                    marginLeft: 14,
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#e6e8eb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                  }}
                                >
                                  {panel.name}
                                </div>
                              ))}
                          </div>
                        );
                      })}
                  </div>
                ))}
              </div>
            ) : !loadingPanels ? (
              <div style={{ padding: '8px 12px' }}>
                <span style={{ color: '#666', fontSize: 13 }}>
                  Nenhum painel disponível para este usuário.
                </span>
              </div>
            ) : null}
          </div>
        </Col>

        {biUrl === null ? (
          <Col md={10}>
            <Col md={3} lg={3} />
            <Col md={3} lg={3}>
              <StyledImg
                src="/images/PoweBiWelcome/Microsoft-Power-BI-Symbol.png"
                alt="Welcome"
                className="welcome-image"
              />
            </Col>
            <Col md={3} lg={3} />
          </Col>
        ) : (
          <Col md={10}>
            {!loading && (
              <Col
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: '2rem',
                }}
              >
                <Button variant="contained" size="small" onClick={toggleIframeFullScreen}>
                  <FullscreenSharpIcon />
                </Button>
              </Col>
            )}

            <div
              ref={reportHostRef}
              style={{
                marginLeft: fullScreen ? '0' : '12px',
                width: '100%',
                height: fullScreen ? '100vh' : '800px',
                background: 'transparent',
              }}
            >
              {currentPanel && biRls === false ? (
                <PowerBiRlsAuth
                  powerBiUrl={biUrl}
                  onLoad={() => setLoading(false)}
                  height={fullScreen ? '100vh' : '800px'}
                  key={iframeKey}
                />
              ) : (
                <iframe
                  ref={iframeRef}
                  key={iframeKey}
                  title="power-bi"
                  src={biUrl}
                  width="100%"
                  height={fullScreen ? '100%' : '800px'}
                  onLoad={() => setLoading(false)}
                  style={{ border: 0, display: 'block' }}
                />
              )}
            </div>
          </Col>
        )}
      </Row>
    </>
  );
}
